import { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { statusLabel } from "../lib/status-labels";
import { isTerminalStatus, validateOpportunityUpdate } from "../lib/validation";
import {
  opportunityStatuses,
  type Opportunity,
  type OpportunityUpdateInput,
} from "../types";

export function QuickUpdateDialog({
  opportunity,
  onClose,
  onSave,
}: {
  opportunity: Opportunity | null;
  onClose: () => void;
  onSave: (input: OpportunityUpdateInput) => Promise<void>;
}) {
  const { locale, t } = useI18n();
  const [input, setInput] = useState<OpportunityUpdateInput>({
    status: "Introduced",
    nextCheckInOn: "",
  });
  const [errors, setErrors] = useState<
    ReturnType<typeof validateOpportunityUpdate>
  >({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opportunity) {
      setInput({
        status: opportunity.status,
        nextCheckInOn: opportunity.nextCheckInOn,
        reviewOn: opportunity.reviewOn,
        files: [],
      });
      setErrors({});
    }
  }, [opportunity]);

  if (!opportunity) return null;
  const requiresReason =
    input.status === "On hold" || input.status === "Closed - not proceeding";
  const isTerminal = isTerminalStatus(input.status);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors = validateOpportunityUpdate(input);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      await onSave(input);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-update-title"
      >
        <div className="dialog-handle" />
        <div className="dialog-title-row">
          <div>
            <span className="eyebrow">
              {opportunity.clientReference.displayName}
            </span>
            <h2 id="quick-update-title">{t("update")}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label={t("cancel")}
          >
            ×
          </button>
        </div>
        <form onSubmit={submit} noValidate>
          <label>
            {t("status")}
            <select
              value={input.status}
              onChange={(event) =>
                setInput({
                  ...input,
                  status: event.target
                    .value as OpportunityUpdateInput["status"],
                })
              }
            >
              {opportunityStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status, locale)}
                </option>
              ))}
            </select>
          </label>
          {!isTerminal && (
            <label>
              {t("nextCheckIn")}
              <input
                type="date"
                value={input.nextCheckInOn}
                onChange={(event) =>
                  setInput({ ...input, nextCheckInOn: event.target.value })
                }
                aria-invalid={Boolean(errors.nextCheckInOn)}
              />
              {errors.nextCheckInOn && (
                <small className="error">{t("requiredCheckIn")}</small>
              )}
            </label>
          )}
          {requiresReason && (
            <label>
              {t("reason")}
              <span className="required">*</span>
              <textarea
                value={input.reason ?? ""}
                onChange={(event) =>
                  setInput({ ...input, reason: event.target.value })
                }
                aria-invalid={Boolean(errors.reason)}
                rows={3}
              />
              {errors.reason && (
                <small className="error">{t("requiredReason")}</small>
              )}
            </label>
          )}
          {input.status === "On hold" && (
            <label>
              {t("reviewDate")}
              <span className="required">*</span>
              <input
                type="date"
                value={input.reviewOn ?? ""}
                onChange={(event) =>
                  setInput({ ...input, reviewOn: event.target.value })
                }
                aria-invalid={Boolean(errors.reviewOn)}
              />
              {errors.reviewOn && (
                <small className="error">{t("requiredReview")}</small>
              )}
            </label>
          )}
          {!requiresReason && (
            <label>
              {t("note")}
              <textarea
                value={input.note ?? ""}
                onChange={(event) =>
                  setInput({ ...input, note: event.target.value })
                }
                rows={3}
              />
            </label>
          )}
          <label className="file-upload">
            {t("addDocuments")}
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
              onChange={(event) =>
                setInput({
                  ...input,
                  files: Array.from(event.target.files ?? []),
                })
              }
              aria-invalid={Boolean(errors.files)}
            />
            <small className="field-hint">{t("documentHint")}</small>
            {errors.files && (
              <small className="error">
                {errors.files === "tooMany"
                  ? t("tooManyFiles")
                  : t("fileTooLarge")}
              </small>
            )}
          </label>
          {(input.files?.length ?? 0) > 0 && (
            <ul className="selected-files" aria-label={t("attachments")}>
              {input.files?.map((file, index) => (
                <li key={`${file.name}-${file.lastModified}`}>
                  <span>
                    {file.name}
                    <small>{formatFileSize(file.size)}</small>
                  </span>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() =>
                      setInput({
                        ...input,
                        files: input.files?.filter(
                          (_, fileIndex) => fileIndex !== index,
                        ),
                      })
                    }
                  >
                    {t("removeFile")}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {isTerminal && <p className="field-hint">{t("terminalHint")}</p>}
          <div className="dialog-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={onClose}
            >
              {t("cancel")}
            </button>
            <button className="primary-button" type="submit" disabled={saving}>
              {t("save")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
